import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { postCustomer } from "../../../../services/experienceOrderService";
import Header from "../../../Checkout/Components/Header/Header";
import Loader from "../../../../components/Loader/Loader";
import Breadcrumbs from "../../../../components/Checkout/Breadcrumbs/BreadCrumbs";
import Footer from "../../../../components/Footer";
import BillingForm from "../../../../components/Checkout/Billing/BillingForm";
import OrderDetails from "../OrderDetails";
import { HelmetProvider } from "react-helmet-async";
import MetaTags from "../../../../components/MetaTags";
import { useCheckoutContext } from "../../../../context/experienceCheckoutContext";
import useExperienceData from "../../../../hooks/useExperienceData";

type BilledErrors = {
    fullName?: string;
    email?: string;
    address?: string;
    zipCode?: string;
    country?: string;
    idType?: string;
    idNumber?: string;
}

type Billed = {
    publicId?: string;
    fullName: string;
    email?: string;
    address?: string;
    zipCode?: string;
    country?: string;
    idType?: string;
    idNumber?: string;
    errors: BilledErrors;
}

const Billing = () => {
    const { experience, promoter } = useExperienceData();
    const { loading, order, fetchOrder } = useCheckoutContext();
    const currentStep = 1;

    const [billed, setBilled] = useState<Billed>({
        fullName: "",
        email: "",
        address: "",
        zipCode: "",
        country: "",
        idType: "",
        idNumber: "",
        errors: {},
    });
    const navigate = useNavigate();
    const [isFormValid, setIsFormValid] = useState(false);
    const [isChecked, setIsChecked] = useState(false);
    const [isSmallScreen, setIsSmallScreen] = useState(false);

    useEffect(() => {
        if (!order && !loading) {
            fetchOrder();
        }
    }, [order, loading, fetchOrder]);

    useEffect(() => {
        if(order && !loading) {
            if (order.customer) {
                setBilled({
                    publicId: order.customer.public_id,
                    fullName: order.customer.full_name,
                    email: order.customer.email,
                    address: order.customer.address,
                    zipCode: order.customer.zipcode,
                    country: order.customer.country,
                    idType: order.customer.id_type,
                    idNumber: order.customer.id_number,
                    errors: {},
                })
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order, loading]);


    useEffect(() => {
        setIsSmallScreen(window.innerWidth <= 768);

        const handleResize = () => {
            setIsSmallScreen(window.innerWidth <= 768);
        };
        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [isSmallScreen]);

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsChecked(event.target.checked);
    };


    const handleBilledChange = (field: keyof Omit<Billed, 'errors'>, value: string) => {
        let newBilled = { ...billed };
        newBilled = { ...newBilled, [field]: value };
        setBilled(newBilled);
    };

    const handleBilledBlur = (field: keyof Omit<Billed, 'errors'>, validateFn: (value: string) => string | undefined) => {
        let newBilled = { ...billed };
        const error = validateFn(newBilled[field]);
        newBilled = {
            ...newBilled,
            errors: { ...newBilled.errors, [field]: error },
        };
        setBilled(newBilled);
    };

    useEffect(() => {
        const formValid =
            billed.fullName &&
            billed.email &&
            billed.address &&
            billed.zipCode &&
            billed.country &&
            billed.idType &&
            billed.idNumber &&
            !billed.errors.fullName &&
            !billed.errors.email &&
            !billed.errors.address &&
            !billed.errors.zipCode &&
            !billed.errors.country &&
            !billed.errors.idType &&
            !billed.errors.idNumber &&
            isChecked;
        setIsFormValid(formValid);
    }, [billed, isChecked]);

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        const customer = {
            full_name: billed.fullName,
            email: billed.email,
            address: billed.address,
            zipcode: billed.zipCode,
            country: billed.country,
            id_type: billed.idType,
            id_number: billed.idNumber,
        };
        if (billed.publicId) {
            customer.public_id = billed.publicId
        }
        await postCustomer(customer, order!.booking.public_id);
        await fetchOrder();


        navigate(`/experiences/${experience?.aliasUrl}/checkout/payments`);
    }

    if (loading || !promoter || !experience) return <Loader />;

    const currentUrl = window.location.protocol + "//" + window.location.host + window.location.pathname + window.location.search

    return (
        <HelmetProvider>
            <MetaTags
                name={promoter.fullname}
                // description={order?.booking?.quotation?.itinerary?.description}
                icon={promoter.logoURL}
                // title={order?.booking?.quotation?.title}
                url={currentUrl}
                font={experience.font ? experience.font : null}
                primaryColor={experience.colors.primary_color ? experience.colors.primary_color : null}
                secondaryColor={experience.colors.secondary_color ? experience.colors.secondary_color : null}
            />
            <Header promoter={promoter}></Header>
            <div className="billing__container">
                <Breadcrumbs currentStep={currentStep} orderStatus={null} noGoBack={true}></Breadcrumbs>
                <div className="main-content">
                    <div className="billing-details__container">
                        <BillingForm
                            data={billed}
                            onChange={handleBilledChange}
                            onBlur={handleBilledBlur}
                        />
                    </div>
                    <OrderDetails
                        handleCheckboxChange={() => { alert("Not implemented") }}
                        handleSubmitNextStep={handleSubmit}
                        isFormValid={true}
                        nextStepLabel={"Siguiente paso"}
                    />
                </div>
                {!isSmallScreen && (
                    <div className="checkout-actions checkout-actions--desktop">
                        <div className="checkbox">
                            <input type="checkbox" onChange={handleCheckboxChange}></input>
                            <label>
                                {/* eslint-disable-next-line */}
                                Acepto <a href="https://trippromoter.com/terminos-y-condiciones/" rel="noreferrer"
                                    target="_blank">los términos y condiciones</a>
                            </label>
                        </div>
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={!isFormValid}
                        >
                            Ir al pago
                        </button>
                    </div>
                )}
            </div>
            <Footer loading={false} promoter={promoter}></Footer>
        </HelmetProvider>
    );
};

export default Billing;
